import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const QuickActions = ({ selectedPair, currentPrice }) => {
  const [quickBuyAmount, setQuickBuyAmount] = useState('');
  const [quickSellAmount, setQuickSellAmount] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(null);

  const quickAmounts = [0.001, 0.01, 0.1, 1];

  const handleQuickTrade = (side, amount) => {
    setShowConfirmation({
      side,
      amount,
      price: currentPrice || 0,
      total: amount * (currentPrice || 0),
      pair: selectedPair || 'BTC/USD'
    });
  };

  const confirmTrade = () => {
    console.log('Quick trade executed:', showConfirmation);
    setShowConfirmation(null);
    // Reset amounts
    setQuickBuyAmount('');
    setQuickSellAmount('');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(price)) {
      return '0.00';
    }
    return Number(price)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Default values for props
  const safeCurrentPrice = currentPrice || 0;
  const safePair = selectedPair || 'BTC/USD';
  const pairParts = safePair?.split('/');
  const baseCurrency = pairParts?.[0] || 'BTC';
  const quoteCurrency = pairParts?.[1] || 'USD';

  return (
    <>
      <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Quick Actions
          </h3>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full"></div>
            <span className="text-xs text-text-secondary">Market Price</span>
            <span className="font-data text-sm text-text-primary">
              ${formatPrice(safeCurrentPrice)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick Buy */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={20} className="text-success" />
              <h4 className="font-heading-semibold text-text-primary">Quick Buy</h4>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Amount ({baseCurrency})
                </label>
                <input
                  type="number"
                  value={quickBuyAmount}
                  onChange={(e) => setQuickBuyAmount(e?.target?.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-border rounded-trading font-data text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary transition-colors duration-trading-fast"
                />
              </div>

              <div className="flex items-center space-x-2">
                {quickAmounts?.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setQuickBuyAmount(amount?.toString())}
                    className="flex-1 py-2 px-3 bg-secondary-100 text-text-secondary rounded-trading font-body-medium text-xs hover:bg-secondary-200 hover:text-text-primary transition-colors duration-trading-fast"
                  >
                    {amount} {baseCurrency}
                  </button>
                ))}
              </div>

              {quickBuyAmount && (
                <div className="p-3 bg-success-50 rounded-trading">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-text-secondary">Estimated Cost:</span>
                    <span className="font-data text-text-primary">
                      ${formatPrice(parseFloat(quickBuyAmount || 0) * safeCurrentPrice)} {quoteCurrency}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Fee (0.1%):</span>
                    <span className="font-data text-text-primary">
                      ${formatPrice(parseFloat(quickBuyAmount || 0) * safeCurrentPrice * 0.001)}
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={() => handleQuickTrade('buy', parseFloat(quickBuyAmount || 0))}
                disabled={!quickBuyAmount || parseFloat(quickBuyAmount || 0) <= 0}
                className="w-full bg-success hover:bg-success-600 disabled:bg-secondary-200 disabled:text-text-secondary text-white py-3 rounded-trading font-body-semibold text-sm transition-colors duration-trading-fast"
              >
                Buy {baseCurrency} at Market Price
              </button>
            </div>
          </div>

          {/* Quick Sell */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingDown" size={20} className="text-error" />
              <h4 className="font-heading-semibold text-text-primary">Quick Sell</h4>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Amount ({baseCurrency})
                </label>
                <input
                  type="number"
                  value={quickSellAmount}
                  onChange={(e) => setQuickSellAmount(e?.target?.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-border rounded-trading font-data text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary transition-colors duration-trading-fast"
                />
              </div>

              <div className="flex items-center space-x-2">
                {quickAmounts?.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setQuickSellAmount(amount?.toString())}
                    className="flex-1 py-2 px-3 bg-secondary-100 text-text-secondary rounded-trading font-body-medium text-xs hover:bg-secondary-200 hover:text-text-primary transition-colors duration-trading-fast"
                  >
                    {amount} {baseCurrency}
                  </button>
                ))}
              </div>

              {quickSellAmount && (
                <div className="p-3 bg-error-50 rounded-trading">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-text-secondary">Estimated Receive:</span>
                    <span className="font-data text-text-primary">
                      ${formatPrice(parseFloat(quickSellAmount || 0) * safeCurrentPrice)} {quoteCurrency}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Fee (0.1%):</span>
                    <span className="font-data text-text-primary">
                      ${formatPrice(parseFloat(quickSellAmount || 0) * safeCurrentPrice * 0.001)}
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={() => handleQuickTrade('sell', parseFloat(quickSellAmount || 0))}
                disabled={!quickSellAmount || parseFloat(quickSellAmount || 0) <= 0}
                className="w-full bg-error hover:bg-error-600 disabled:bg-secondary-200 disabled:text-text-secondary text-white py-3 rounded-trading font-body-semibold text-sm transition-colors duration-trading-fast"
              >
                Sell {baseCurrency} at Market Price
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-trading shadow-trading-modal max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading-semibold text-text-primary">
                Confirm {showConfirmation?.side === 'buy' ? 'Buy' : 'Sell'} Order
              </h3>
              <button
                onClick={() => setShowConfirmation(null)}
                className="p-2 rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
              >
                <Icon name="X" size={20} className="text-text-secondary" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-secondary-50 rounded-trading">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-secondary">Pair:</span>
                    <div className="font-body-semibold text-text-primary">
                      {showConfirmation?.pair || 'BTC/USD'}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Type:</span>
                    <div className="font-body-semibold text-text-primary capitalize">
                      Market {showConfirmation?.side || 'buy'}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Amount:</span>
                    <div className="font-data text-text-primary">
                      {showConfirmation?.amount || 0} {(showConfirmation?.pair || 'BTC/USD')?.split('/')?.[0]}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Price:</span>
                    <div className="font-data text-text-primary">
                      ${formatPrice(showConfirmation?.price || 0)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-border rounded-trading">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-text-secondary">
                    {showConfirmation?.side === 'buy' ? 'Total Cost:' : 'Total Receive:'}
                  </span>
                  <span className="font-data text-lg text-text-primary">
                    ${formatPrice(showConfirmation?.total || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-text-secondary">Trading Fee (0.1%):</span>
                  <span className="font-data text-text-primary">
                    ${formatPrice((showConfirmation?.total || 0) * 0.001)}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={confirmTrade}
                  className={`flex-1 py-3 rounded-trading font-body-semibold text-sm transition-colors duration-trading-fast ${
                    showConfirmation?.side === 'buy' ?'bg-success hover:bg-success-600 text-white' :'bg-error hover:bg-error-600 text-white'
                  }`}
                >
                  Confirm {showConfirmation?.side === 'buy' ? 'Buy' : 'Sell'}
                </button>
                <button
                  onClick={() => setShowConfirmation(null)}
                  className="flex-1 bg-secondary-100 text-text-primary py-3 rounded-trading font-body-semibold text-sm hover:bg-secondary-200 transition-colors duration-trading-fast"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActions;